/**
 * features/finance/BankReconciliation.tsx
 * ------------------------------------------------------------------
 * FINANCE — bank-statement reconciliation.
 *
 * 1. Import a bank statement CSV.
 * 2. Map the Date / Description / Amount / Reference columns (auto-guessed).
 * 3. Review auto-matches — money-in lines matched to open invoices by
 *    invoice number, then by student name. Fix ambiguous/unmatched lines
 *    with a manual invoice picker.
 * 4. Post the matched lines as EFT payments — reusing the exact posting
 *    path (applyPayment + receipt number + invoice update + audit).
 *
 * Idempotent: each line carries a stable id stored on the payment
 * (Payment.statementLineId); re-importing the same statement shows those
 * lines as already posted and never double-charges.
 * ------------------------------------------------------------------ */
import { useMemo, useRef, useState } from 'react';
import { ArrowLeftRight, Upload, CheckCircle2, HelpCircle, CircleSlash, FileCheck2, Search, X } from 'lucide-react';
import { GradientHero, SoftCard, StatChip, glassFieldClass } from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { useAuth } from '@/contexts/AuthContext';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { finance } from '@/features/finance/financeWrite';
import { recordFinanceAudit } from '@/lib/financeAudit';
import { applyPayment, formatReceiptNo, fmtPula, type Invoice } from '@/lib/financeCore';
import {
  parseCsv, buildStatementLines, matchStatement, guessColumnMap,
  type ColumnMap, type StatementLine, type MatchResult,
} from '@/lib/bankRecon';

type Step = 'upload' | 'map' | 'review';

const STATUS_META: Record<MatchResult['status'], { label: string; cls: string }> = {
  matched: { label: 'Matched', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  ambiguous: { label: 'Needs choice', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  unmatched: { label: 'No match', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-300' },
  posted: { label: 'Already posted', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
};

export default function BankReconciliation() {
  const data = useFinanceData();
  const { userData } = useAuth();
  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email };
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [map, setMap] = useState<ColumnMap | null>(null);
  const [lines, setLines] = useState<StatementLine[]>([]);
  const [choice, setChoice] = useState<Record<string, string>>({}); // lineId -> invoiceId ('' = skip)
  const [busy, setBusy] = useState(false);
  const [sc, setSc] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>({ show: false, status: 'saving', message: '' });

  const invoiceById = useMemo(() => {
    const m = new Map<string, Invoice>();
    for (const inv of data.invoices) m.set(inv.id, inv);
    return m;
  }, [data.invoices]);

  const openInvoices = useMemo(
    () => data.invoices
      .filter((i) => i.status !== 'void' && i.balance > 0)
      .sort((a, b) => (a.studentName ?? '').localeCompare(b.studentName ?? '')),
    [data.invoices],
  );

  const postedLineIds = useMemo(
    () => new Set(data.payments.map((p) => p.statementLineId).filter(Boolean) as string[]),
    [data.payments],
  );

  const results = useMemo(
    () => (lines.length ? matchStatement(lines, data.invoices, postedLineIds) : []),
    [lines, data.invoices, postedLineIds],
  );

  // Effective chosen invoice for a line: user override, else the auto-match.
  const chosenFor = (r: MatchResult): string =>
    (r.line.id in choice ? choice[r.line.id] : (r.status === 'matched' ? (r.invoiceId ?? '') : ''));

  const summary = useMemo(() => {
    let matched = 0, ambiguous = 0, unmatched = 0, posted = 0, postable = 0, total = 0;
    for (const r of results) {
      if (r.status === 'posted') { posted++; continue; }
      if (r.status === 'matched') matched++;
      else if (r.status === 'ambiguous') ambiguous++;
      else unmatched++;
      const inv = invoiceById.get(chosenFor(r));
      if (inv && inv.balance > 0) { postable++; total += r.line.amount; }
    }
    return { matched, ambiguous, unmatched, posted, postable, total };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, choice, invoiceById]);

  /* --------------------------- handlers --------------------------- */

  function onFile(file: File) {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCsv(String(reader.result ?? ''));
      if (parsed.headers.length === 0) {
        setSc({ show: true, status: 'error', message: 'That file has no readable rows.' });
        return;
      }
      setHeaders(parsed.headers);
      setRows(parsed.rows);
      setMap(guessColumnMap(parsed.headers));
      setLines([]);
      setChoice({});
      setStep('map');
    };
    reader.onerror = () => setSc({ show: true, status: 'error', message: 'Could not read the file.' });
    reader.readAsText(file);
  }

  function runMatch() {
    if (!map) return;
    const built = buildStatementLines(rows, map);
    setLines(built);
    setChoice({});
    setStep('review');
  }

  async function postAll() {
    setBusy(true);
    setSc({ show: true, status: 'saving', message: 'Posting matched payments…' });
    try {
      let seq = data.payments.length;
      let posted = 0;
      for (const r of results) {
        if (r.status === 'posted') continue;
        const invId = chosenFor(r);
        const invoice = invId ? invoiceById.get(invId) : undefined;
        if (!invoice || invoice.balance <= 0) continue;
        seq += 1;
        const receiptNo = formatReceiptNo(invoice.year, seq);
        const upd = applyPayment(invoice, r.line.amount);
        const receivedAt = parseLineDate(r.line.date);
        await finance.payments.add({
          invoiceId: invoice.id, studentId: invoice.studentId, studentName: invoice.studentName,
          familyId: invoice.familyId, amount: r.line.amount, method: 'eft',
          reference: r.line.reference || undefined, receiptNo, receivedAt,
          recordedBy: actor.id, note: `Bank import: ${r.line.description}`.slice(0, 180),
          statementLineId: r.line.id,
        });
        await finance.invoices.update(invoice.id, upd);
        await recordFinanceAudit({ action: 'payment.reconcile', actor, subjectId: invoice.id, after: { amount: r.line.amount, receiptNo, statementLineId: r.line.id } });
        posted += 1;
      }
      setSc({ show: true, status: 'success', message: posted > 0 ? `Posted ${posted} payment${posted === 1 ? '' : 's'} from the statement.` : 'Nothing to post — choose invoices for the matched lines first.' });
    } catch (e) {
      console.error(e);
      setSc({ show: true, status: 'error', message: 'Posting failed partway — re-import to resume; already-posted lines are skipped.' });
    } finally {
      setBusy(false);
    }
  }

  /* ----------------------------- render --------------------------- */

  if (data.loading) {
    return (
      <div className="space-y-6">
        <GradientHero icon={ArrowLeftRight} title="Bank reconciliation" subtitle="Match a bank statement to open invoices" />
        <div className="flex justify-center py-16"><Spinner label="Loading finance data…" /></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SaveConfirmation show={sc.show} status={sc.status} message={sc.message} onDismiss={() => setSc((s) => ({ ...s, show: false }))} />
      <GradientHero icon={ArrowLeftRight} title="Bank reconciliation" subtitle="Import a statement, match credits to invoices, post payments" />

      {/* STEP 1 — upload */}
      {step === 'upload' && (
        <SoftCard hover={false}>
          <div className="flex flex-col items-center text-center py-10 gap-3">
            <div className="w-14 h-14 rounded-2xl bg-[var(--accent-strong,#1b3a6b)]/10 flex items-center justify-center">
              <Upload className="text-[var(--accent-link,#2d5295)]" />
            </div>
            <div>
              <p className="font-semibold text-gray-800 dark:text-gray-100">Import a bank statement (CSV)</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mt-1">
                Export your statement as CSV from online banking. Only money-in (credit) lines are
                considered — debits and zero lines are ignored.
              </p>
            </div>
            <input
              ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }}
            />
            <button onClick={() => fileRef.current?.click()} className="mt-1 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[var(--accent-strong,#1b3a6b)] hover:bg-[var(--accent-strong-hover,#162e52)] rounded-xl">
              <Upload size={15} /> Choose CSV file
            </button>
          </div>
        </SoftCard>
      )}

      {/* STEP 2 — column mapping */}
      {step === 'map' && map && (
        <SoftCard hover={false}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-semibold text-gray-800 dark:text-gray-100">Map the columns</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{fileName} · {rows.length} rows</p>
            </div>
            <button onClick={() => setStep('upload')} className="text-xs text-gray-500 hover:underline">Choose a different file</button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ColPick label="Date" headers={headers} value={map.date} onChange={(v) => setMap({ ...map, date: v })} />
            <ColPick label="Description / narrative" headers={headers} value={map.description} onChange={(v) => setMap({ ...map, description: v })} />
            <ColPick label="Amount" headers={headers} value={map.amount} onChange={(v) => setMap({ ...map, amount: v })} />
            <ColPick label="Reference (optional)" headers={headers} value={map.reference} onChange={(v) => setMap({ ...map, reference: v })} allowNone />
          </div>

          <label className="mt-4 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input type="checkbox" checked={map.amountIsCreditOnly} onChange={(e) => setMap({ ...map, amountIsCreditOnly: e.target.checked })} />
            The amount column is money-in only (otherwise it's signed — negatives are treated as debits and skipped).
          </label>

          {/* preview */}
          <div className="mt-4 overflow-x-auto rounded-xl border border-gray-100 dark:border-gray-700">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-800/60 text-gray-500">
                <tr>{headers.map((h, i) => <th key={i} className="px-3 py-2 text-left font-medium whitespace-nowrap">{h || `Col ${i + 1}`}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                {rows.slice(0, 4).map((r, ri) => (
                  <tr key={ri}>{headers.map((_, ci) => <td key={ci} className="px-3 py-1.5 text-gray-600 dark:text-gray-300 whitespace-nowrap">{r[ci]}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex justify-end">
            <button onClick={runMatch} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[var(--accent-strong,#1b3a6b)] hover:bg-[var(--accent-strong-hover,#162e52)] rounded-xl">
              <ArrowLeftRight size={15} /> Match against invoices
            </button>
          </div>
        </SoftCard>
      )}

      {/* STEP 3 — review */}
      {step === 'review' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatChip tone="emerald" icon={CheckCircle2} value={String(summary.matched)} label="auto-matched" />
            <StatChip tone="gold" icon={HelpCircle} value={String(summary.ambiguous)} label="need a choice" />
            <StatChip tone="slate" icon={CircleSlash} value={String(summary.unmatched)} label="no match" />
            <StatChip tone="blue" icon={FileCheck2} value={String(summary.posted)} label="already posted" />
          </div>

          <SoftCard pad="p-0" hover={false}>
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">{fileName} · {lines.length} credit lines</div>
              <button onClick={() => setStep('map')} className="text-xs text-gray-500 hover:underline">Back to mapping</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="py-2 px-4 font-medium">Date</th>
                    <th className="py-2 px-4 font-medium">Statement line</th>
                    <th className="py-2 px-4 font-medium text-right">Amount</th>
                    <th className="py-2 px-4 font-medium">Status</th>
                    <th className="py-2 px-4 font-medium">Apply to invoice</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {results.map((r) => {
                    const meta = STATUS_META[r.status];
                    const isPosted = r.status === 'posted';
                    return (
                      <tr key={r.line.id} className={isPosted ? 'opacity-60' : ''}>
                        <td className="py-2.5 px-4 whitespace-nowrap text-gray-600 dark:text-gray-300">{r.line.date}</td>
                        <td className="py-2.5 px-4">
                          <div className="text-gray-800 dark:text-gray-100 max-w-xs truncate" title={r.line.description}>{r.line.description || '—'}</div>
                          {r.line.reference && <div className="text-xs text-gray-400">ref: {r.line.reference}</div>}
                        </td>
                        <td className="py-2.5 px-4 text-right font-semibold tabular-nums text-gray-900 dark:text-white">{fmtPula(r.line.amount)}</td>
                        <td className="py-2.5 px-4"><span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${meta.cls}`}>{meta.label}</span></td>
                        <td className="py-2.5 px-4">
                          {isPosted ? (
                            <span className="text-xs text-gray-400">posted</span>
                          ) : (
                            <InvoiceSearchPicker
                              openInvoices={openInvoices}
                              invoiceById={invoiceById}
                              valueId={chosenFor(r)}
                              candidates={r.candidates}
                              seedQuery={smartQuery(`${r.line.description} ${r.line.reference}`)}
                              onChange={(id) => setChoice((c) => ({ ...c, [r.line.id]: id }))}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </SoftCard>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {summary.postable} line{summary.postable === 1 ? '' : 's'} ready · {fmtPula(summary.total)} to post as EFT payments.
            </p>
            <button
              onClick={postAll}
              disabled={busy || summary.postable === 0}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl disabled:opacity-50"
            >
              <CheckCircle2 size={16} /> Post {summary.postable} payment{summary.postable === 1 ? '' : 's'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* ----------------------------- helpers ----------------------------- */

/** Strip bank-statement noise words to a likely student-name search seed. */
const NOISE = new Set([
  'ib', 'transfer', 'from', 'credit', 'cr', 'eft', 'rtgs', 'cash', 'deposit', 'dep', 'internet',
  'banking', 'bank', 'purchase', 'payment', 'pmt', 'for', 'school', 'fees', 'fee', 'term', 'one',
  'two', 'three', 'as', 'level', 'pos', 'ref', 'inv', 't1', 't2', 't3', 'monthly', 'service',
  'charges', 'charge', 'lkc', 'and', 'the', 'mr', 'mrs', 'ms', 'miss',
]);
function smartQuery(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z ]/g, ' ')
    .split(/\s+/)
    .filter((t) => t.length >= 3 && !NOISE.has(t))
    .slice(0, 3)
    .join(' ');
}

/** Searchable invoice picker — type a student name or invoice number to attach. */
function InvoiceSearchPicker({ openInvoices, invoiceById, valueId, candidates, seedQuery, onChange }: {
  openInvoices: Invoice[];
  invoiceById: Map<string, Invoice>;
  valueId: string;
  candidates: string[];
  seedQuery: string;
  onChange: (id: string) => void;
}) {
  const chosen = valueId ? invoiceById.get(valueId) : undefined;
  const [editing, setEditing] = useState(false);
  const [focused, setFocused] = useState(false);
  const [query, setQuery] = useState(() => (!valueId && candidates.length === 0 ? seedQuery : ''));

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      const cand = candidates.map((id) => invoiceById.get(id)).filter(Boolean) as Invoice[];
      return (cand.length ? cand : openInvoices).slice(0, 8);
    }
    const tokens = q.split(/\s+/);
    const t0 = tokens[0];
    return openInvoices
      .filter((inv) => {
        const hay = `${inv.studentName ?? ''} ${inv.invoiceNo}`.toLowerCase();
        return tokens.every((t) => hay.includes(t));
      })
      .sort((a, b) =>
        Number((b.studentName ?? '').toLowerCase().startsWith(t0)) -
        Number((a.studentName ?? '').toLowerCase().startsWith(t0)))
      .slice(0, 8);
  }, [query, openInvoices, candidates, invoiceById]);

  if (chosen && !editing) {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200">
          <span className="font-medium">{chosen.studentName ?? 'Unknown'}</span>
          <span className="text-emerald-600 dark:text-emerald-400">· {chosen.invoiceNo} · {fmtPula(chosen.balance)}</span>
        </span>
        <button onClick={() => { setEditing(true); setQuery(seedQuery); }} className="text-xs text-gray-500 hover:underline">change</button>
        <button onClick={() => onChange('')} title="Skip this line" className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
      </div>
    );
  }

  const showDrop = focused || query.length > 0 || candidates.length > 0;
  return (
    <div className="relative min-w-[18rem]">
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          placeholder="Search student or invoice #…"
          className={glassFieldClass('pl-8 py-1 text-xs w-full')}
        />
      </div>
      {showDrop && (
        <div className="absolute z-20 mt-1 w-full max-h-60 overflow-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
          {query.trim() === '' && candidates.length > 0 && (
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700">Suggested</div>
          )}
          {results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-400">No open invoice matches “{query}”.</div>
          ) : (
            results.map((inv) => (
              <button
                key={inv.id}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onChange(inv.id); setEditing(false); setFocused(false); }}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <div className="text-xs font-medium text-gray-800 dark:text-gray-100">{inv.studentName ?? 'Unknown'}</div>
                <div className="text-[11px] text-gray-400">{inv.invoiceNo} · {inv.term} {inv.year} · bal {fmtPula(inv.balance)}</div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/** Best-effort parse of a statement date to an ISO timestamp (noon, local). */
function parseLineDate(s: string): string {
  const t = s.trim();
  // dd/mm/yyyy or dd-mm-yyyy
  const dmy = t.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    const year = y.length === 2 ? `20${y}` : y;
    const iso = `${year}-${m.padStart(2, '0')}-${d.padStart(2, '0')}T12:00:00`;
    const dt = new Date(iso);
    if (!Number.isNaN(dt.getTime())) return dt.toISOString();
  }
  const dt = new Date(t);
  return Number.isNaN(dt.getTime()) ? new Date().toISOString() : dt.toISOString();
}

function ColPick({ label, headers, value, onChange, allowNone }: {
  label: string; headers: string[]; value: number; onChange: (v: number) => void; allowNone?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">{label}</span>
      <select value={value} onChange={(e) => onChange(Number(e.target.value))} className={glassFieldClass('w-full')}>
        {allowNone && <option value={-1}>— none —</option>}
        {headers.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
      </select>
    </label>
  );
}
